export const FilterType = {
  Contains: 1,
  LessThanOrEqual: 2,
  Equal: 3,
  GreaterThan: 4,
  GreaterThanOrEqual: 5,
  NotEqual: 6,
  LessThan: 7
} as const;

export type FilterTypeName = keyof typeof FilterType;

export type SortDirection = "ASC" | "DESC";

export type GridFilter = {
  fieldName: string;
  type: FilterTypeName;
  value: string;
  isMultiSet?: boolean;
  isQuick?: boolean;
};

export type GridQuery = {
  skip: number;
  take: number;
  sortField: string;
  sortDirection: SortDirection;
  filters: GridFilter[];
  isAnd?: boolean;
};

export type EntityCollection<TRow> = {
  entities: TRow[];
  count: number;
  totalCount: number;
};

export type GridPage<TRow> = {
  rows: TRow[];
  total: number;
};

export type GridLoader<TRow> = (query: GridQuery, signal: AbortSignal) => Promise<GridPage<TRow>>;

export function base64Utf8(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";

  for (const byte of bytes) binary += String.fromCharCode(byte);

  return btoa(binary);
}

function buildFilter(filter: GridFilter): string {
  return [`Type=${filter.type}`, `IsMultiSet=${filter.isMultiSet ? "true" : "false"}`, `IsQuick=${filter.isQuick ? "true" : "false"}`, `FieldName=${encodeURIComponent(filter.fieldName)}`, `Value=${encodeURIComponent(base64Utf8(filter.value))}`].join("&");
}

export function buildPaging(query: GridQuery): string {
  const parts = [`Skip=${query.skip}`, `Take=${query.take}`, `IsAnd=${query.isAnd === false ? "false" : "true"}`, `Sorting.OrderBy=${query.sortDirection}`, `Sorting.Field=${encodeURIComponent(query.sortField)}`];

  for (const filter of query.filters) {
    if (filter.value === "" || filter.value === null || filter.value === undefined) continue;

    parts.push(`Filter=${encodeURIComponent(buildFilter(filter))}`);
  }

  return parts.join("&");
}

export function pagingQueryString(query: GridQuery): string {
  return `paging=${encodeURIComponent(buildPaging(query))}`;
}

export function filterKey(filters: GridFilter[]): string {
  return filters.map(a => `${a.fieldName} ${a.type} ${a.value} ${a.isMultiSet ? "1" : "0"} ${a.isQuick ? "1" : "0"}`).join("\u0000");
}

export function toGridPage<TRow>(collection: EntityCollection<TRow> | null | undefined): GridPage<TRow> {
  return { rows: collection?.entities ?? [], total: collection?.totalCount ?? 0 };
}

export type FieldReader<TRow> = (row: TRow, field: string) => unknown;

export function readField<TRow>(row: TRow, field: string): unknown {
  return field.split(".").reduce<unknown>((current, part) => {
    if (current === null || current === undefined) return current;

    const key = part.charAt(0).toLowerCase() + part.slice(1);
    const record = current as Record<string, unknown>;

    return key in record ? record[key] : record[part];
  }, row);
}

function compareValues(a: unknown, b: unknown): number {
  if (a === null || a === undefined) return b === null || b === undefined ? 0 : 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function matchesFilter(value: unknown, filter: GridFilter): boolean {
  const text = value === null || value === undefined ? "" : String(value);
  const needle = filter.value;

  switch (filter.type) {
    case "Contains":
      return text.toLocaleLowerCase().includes(needle.toLocaleLowerCase());
    case "Equal":
      return typeof value === "boolean" ? String(value) === needle.toLowerCase() : text.toLocaleLowerCase() === needle.toLocaleLowerCase();
    case "NotEqual":
      return typeof value === "boolean" ? String(value) !== needle.toLowerCase() : text.toLocaleLowerCase() !== needle.toLocaleLowerCase();
    case "GreaterThan":
      return compareValues(value, coerce(value, needle)) > 0;
    case "GreaterThanOrEqual":
      return compareValues(value, coerce(value, needle)) >= 0;
    case "LessThan":
      return compareValues(value, coerce(value, needle)) < 0;
    case "LessThanOrEqual":
      return compareValues(value, coerce(value, needle)) <= 0;
  }
}

function coerce(sample: unknown, text: string): unknown {
  if (typeof sample === "number") {
    const parsed = Number(text);

    return Number.isFinite(parsed) ? parsed : text;
  }

  return text;
}

export function queryRows<TRow>(rows: TRow[], query: GridQuery, read: FieldReader<TRow> = readField): GridPage<TRow> {
  const quick = query.filters.filter(a => a.isQuick && a.value !== "");
  const plain = query.filters.filter(a => !a.isQuick && a.value !== "");
  const isAnd = query.isAnd !== false;

  const filtered = rows.filter(row => {
    const plainOk = plain.length === 0 ? true : isAnd ? plain.every(a => matchesFilter(read(row, a.fieldName), a)) : plain.some(a => matchesFilter(read(row, a.fieldName), a));
    const quickOk = quick.length === 0 ? true : quick.some(a => matchesFilter(read(row, a.fieldName), a));

    return plainOk && quickOk;
  });

  const sign = query.sortDirection === "DESC" ? -1 : 1;
  const sorted = query.sortField.length === 0 ? filtered : [...filtered].sort((a, b) => compareValues(read(a, query.sortField), read(b, query.sortField)) * sign);

  return { rows: sorted.slice(query.skip, query.skip + query.take), total: sorted.length };
}

export function memoryLoader<TRow>(rows: TRow[], read?: FieldReader<TRow>): GridLoader<TRow> {
  return query => Promise.resolve(queryRows(rows, query, read));
}
